import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Loader2, MapPin, Mountain, Ruler, Calendar } from 'lucide-react';

function formatUtmbDate(raw: string | null | undefined, locale: string): string {
    if (!raw) return '';
    const date = new Date(raw);
    if (isNaN(date.getTime())) return raw;
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}
import { cn } from '@/lib/utils';
import { RaceFormat } from '@/gql/graphql';
import type { SearchUtmbRacesQuery } from '@/gql/graphql';
import { SearchBar } from '@/components/ui/SearchBar';
import { SEARCH_UTMB_RACES } from '@/graphql/queries';

type UtmbRace = NonNullable<SearchUtmbRacesQuery['searchUtmbRaces']>[number];

const CATEGORY_FILTERS = [
    { value: RaceFormat['20K'], label: '20K' },
    { value: RaceFormat['50K'], label: '50K' },
    { value: RaceFormat['100K'], label: '100K' },
    { value: RaceFormat['100M'], label: '100M' },
] as const;

interface UtmbRaceSearchProps {
    onSelect: (race: UtmbRace) => void;
}

export function UtmbRaceSearch({ onSelect }: UtmbRaceSearchProps) {
    const { t, i18n } = useTranslation('races');
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounce(search, 300);
    const [selectedCategories, setSelectedCategories] = useState<RaceFormat[]>([]);

    const { data, loading, error } = useQuery(SEARCH_UTMB_RACES, {
        variables: {
            search: debouncedSearch || undefined,
            categories: selectedCategories.length > 0 ? selectedCategories : undefined,
            limit: 20,
        },
    });

    const races = data?.searchUtmbRaces ?? [];

    const toggleCategory = (cat: RaceFormat) => {
        setSelectedCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    return (
        <div className="space-y-4">
            <SearchBar
                placeholder={t('create.utmbSearch.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => setSearch('')}
                isLoading={loading}
            />

            {/* Category filters */}
            <div className="flex gap-2">
                {CATEGORY_FILTERS.map(({ value, label }) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => toggleCategory(value)}
                        className={cn(
                            "px-3 py-1.5 rounded-lg text-xs font-bold transition-all border",
                            selectedCategories.includes(value)
                                ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                                : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                        )}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Results */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {error && (
                    <p className="text-sm text-red-500 text-center py-4">
                        {t('create.utmbSearch.error')}
                    </p>
                )}

                {!loading && !error && races.length === 0 && debouncedSearch && (
                    <p className="text-sm text-slate-400 text-center py-8">
                        {t('create.utmbSearch.empty')}
                    </p>
                )}

                {loading && races.length === 0 && (
                    <div className="flex justify-center py-8">
                        <Loader2 size={24} className="animate-spin text-emerald-600" />
                    </div>
                )}

                {races.map((race) => (
                    <button
                        key={race.uri}
                        type="button"
                        onClick={() => onSelect(race)}
                        className="w-full text-left p-4 rounded-xl border border-slate-100 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 transition-all"
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-slate-900 truncate">{race.name}</p>
                                <p className="text-xs text-slate-500 truncate">{race.eventName}</p>
                            </div>
                            <span className="shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-600 uppercase">
                                {race.category}
                            </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                                <MapPin size={12} />
                                {race.startPlace}
                            </span>
                            <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {formatUtmbDate(race.startDate, i18n.language)}
                            </span>
                            <span className="flex items-center gap-1">
                                <Ruler size={12} />
                                {race.distance} km
                            </span>
                            <span className="flex items-center gap-1">
                                <Mountain size={12} />
                                {race.elevationGain} m
                            </span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
